import DraftBoard from '@/components/draft/DraftBoard';
import { getBoardCellColor } from '@/lib/nflDraftBoardColors';
import type { DraftPickLog } from '@/types/draftPlayer';
import { cn } from '@/lib/utils';

/**
 * Prompt 5: the BOARD half of the center column.
 *
 * This is a THIN WRAPPER over the shipped DraftBoard (SAB-847), not a new grid. That
 * component already does rounds-down/teams-across, the detailed cell (name, position, team,
 * bye, round.pick, snake arrow), the colour resolver and blank-unpicked. Rebuilding it here
 * would mean two NFL board implementations free to drift, and would put MLB's guard test in
 * front of a component that no longer backs the MLB path.
 *
 * ── SAB-845, PRESERVED ──────────────────────────────────────────────────────
 *
 * While reverse_order or skip_pick is live, `blankUnpicked` blanks every UNPICKED cell and a
 * banner explains why. The design draws upcoming-pick cells ("YOU · 2.04") and column
 * headers asserting team ownership; both are unsafe during an order effect, because
 * nfl_draft_picks holds base snake order and only the current row is rewritten at pick time.
 * Made picks are unaffected -- their rows were rewritten when they landed -- so history
 * stays readable while the future stops lying.
 */

export interface BoardPanelProps {
  pickLogs: DraftPickLog[];
  teams: { id: string; name: string }[];
  totalRounds: number;
  currentPick: number;
  draftOrder: { id: string; team_id: string; round: number; pick_number: number; round_pick: number }[];
  /** SAB-845: true while an order-rewriting sabotage is active. */
  orderScrambled: boolean;
  skippedPickNumbers?: Set<number>;
}

export default function BoardPanel({
  pickLogs, teams, totalRounds, currentPick, draftOrder, orderScrambled, skippedPickNumbers,
}: BoardPanelProps) {
  return (
    <div data-testid="board-panel" className="flex-1 min-h-0 flex flex-col">
      {orderScrambled && (
        <div
          data-testid="board-scrambled-banner"
          className="flex-none border-b border-damage/40 bg-damage/10 px-4 py-2 text-[11.5px] text-damage"
        >
          A sabotage is rewriting the draft order. Upcoming picks are hidden until it clears —
          made picks are unaffected.
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        <DraftBoard
          picks={pickLogs}
          teams={teams.map(t => ({ id: t.id, name: t.name, owner: '' }))}
          totalRounds={totalRounds}
          currentPick={currentPick}
          viewMode="grid"
          draftOrder={draftOrder}
          skippedPickNumbers={skippedPickNumbers}
          fillHeight
          expanded
          detailedCells
          blankUnpicked={orderScrambled}
          colorFor={getBoardCellColor}
        />
      </div>

      <BoardLegend />
    </div>
  );
}

function Swatch({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn('h-[9px] w-[9px] rounded-sm', className)} />
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </span>
  );
}

/** Pinned to the bottom of the column, per the design. */
function BoardLegend() {
  return (
    <div
      data-testid="board-legend"
      className="flex-none flex items-center gap-4 border-t border-border bg-card px-4 py-2"
    >
      <span className="font-display text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
        Key
      </span>
      <Swatch className="bg-primary" label="Your pick" />
      <Swatch className="bg-[#7EB0F7]" label="Autopicked" />
      <Swatch className="bg-damage" label="Sabotaged" />
      <Swatch className="bg-gold" label="On the clock" />
    </div>
  );
}
